/**
 * PricingPage — shown full-screen when a logged-in user has no active subscription.
 *
 * Used in two ways:
 *   1. Full-screen gate (from AuthGate) before entering the app
 *   2. Overlay modal (from AppInner) when a 402 fires mid-session
 *
 * Props:
 *   onSubscribed — called after returning from Stripe with ?checkout=success
 *   onDismiss    — optional, used in overlay mode (not shown in gate mode)
 *   overlay      — boolean, true = modal overlay, false (default) = full-screen page
 */

import React, { useEffect, useState } from 'react';
import { theme } from '../theme';
import { useAuth } from '../context/AuthContext';
import { apiGet, apiPost } from '../api';

export default function PricingPage({ onSubscribed, onDismiss, overlay = false }) {
  const { user, signOut } = useAuth();
  const [plans, setPlans]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [checking, setChecking] = useState(null);
  const [error, setError]       = useState(null);

  // Handle return from Stripe checkout (?checkout=success)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('checkout') === 'success') {
      window.history.replaceState({}, '', window.location.pathname);
      // Re-check subscription status and call onSubscribed if confirmed
      apiGet('/billing/status').then(({ hasAccess }) => {
        if (hasAccess && onSubscribed) onSubscribed();
      }).catch(() => {});
    }
  }, [onSubscribed]);

  useEffect(() => {
    apiGet('/billing/prices')
      .then(data => setPlans(data.plans || []))
      .catch(() => setError('Unable to load plans. Please try again.'))
      .finally(() => setLoading(false));
  }, []);

  async function handleSubscribe(priceId) {
    setChecking(priceId);
    setError(null);
    try {
      const { url } = await apiPost('/billing/checkout', { priceId });
      window.location.href = url;
    } catch (err) {
      setError(err.message || 'Failed to start checkout. Please try again.');
      setChecking(null);
    }
  }

  async function handleManageBilling() {
    setChecking('portal');
    setError(null);
    try {
      const { url } = await apiPost('/billing/portal', {});
      window.location.href = url;
    } catch (err) {
      setError(err.message || 'Failed to open billing portal.');
      setChecking(null);
    }
  }

  const card = (
    <div style={{
      background: theme.surface,
      border: `1px solid ${theme.border}`,
      borderRadius: 16,
      width: '100%', maxWidth: 520,
      padding: '36px 32px',
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20,
      textAlign: 'center',
      maxHeight: overlay ? '90vh' : undefined,
      overflowY: overlay ? 'auto' : undefined,
    }}>
      {/* Icon */}
      <div style={{
        width: 56, height: 56, borderRadius: '50%',
        background: `${theme.accent}18`,
        border: `2px solid ${theme.accent}44`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 24,
      }}>🔒</div>

      <div>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: theme.text, marginBottom: 8 }}>
          Subscribe to FusionQuery
        </h2>
        <p style={{ fontSize: 14, color: theme.textMuted, lineHeight: 1.6 }}>
          Unlock unlimited Oracle Fusion SQL queries with AI-powered generation.
          {user?.email && (
            <> Signed in as <strong style={{ color: theme.text }}>{user.email}</strong>.</>
          )}
        </p>
      </div>

      {/* Plans */}
      {loading && (
        <p style={{ color: theme.textMuted, fontSize: 14 }}>Loading plans…</p>
      )}

      {!loading && plans.length > 0 && (
        <div style={{ display: 'flex', gap: 12, width: '100%', flexWrap: 'wrap' }}>
          {plans.map(plan => (
            <PlanCard
              key={plan.id}
              plan={plan}
              disabled={!!checking}
              loading={checking === plan.priceId}
              onSubscribe={() => handleSubscribe(plan.priceId)}
            />
          ))}
        </div>
      )}

      {error && (
        <p style={{
          fontSize: 13, color: '#ef4444',
          background: '#ef444412',
          border: '1px solid #ef444430',
          borderRadius: 8, padding: '10px 14px', width: '100%',
        }}>
          {error}
        </p>
      )}

      {/* Trial note */}
      <p style={{ fontSize: 12, color: theme.textMuted }}>
        ✨ Includes a <strong>7-day free trial</strong> — no charge until your trial ends.
      </p>

      {/* Footer */}
      <div style={{ width: '100%', borderTop: `1px solid ${theme.border}`, paddingTop: 12 }}>
        <p style={{ fontSize: 12, color: theme.textMuted, marginBottom: 10 }}>
          Already subscribed?{' '}
          <button
            onClick={handleManageBilling}
            disabled={!!checking}
            style={{
              background: 'none', border: 'none', color: theme.accent,
              cursor: checking ? 'not-allowed' : 'pointer', fontSize: 12, padding: 0,
              textDecoration: 'underline',
            }}
          >
            {checking === 'portal' ? 'Opening…' : 'Manage billing'}
          </button>
        </p>

        <button onClick={signOut} style={{
          background: 'none', border: `1px solid ${theme.border}`,
          borderRadius: 8, padding: '9px 16px', fontSize: 13,
          color: theme.textMuted, cursor: 'pointer', width: '100%',
        }}>
          Sign out
        </button>
      </div>
    </div>
  );

  if (overlay) {
    return (
      <div style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.88)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 2000, padding: 24,
      }}>
        {card}
      </div>
    );
  }

  // Full-screen (gate mode)
  return (
    <div style={{
      minHeight: '100vh', background: theme.bg,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 24,
    }}>
      {card}
    </div>
  );
}

function PlanCard({ plan, onSubscribe, disabled, loading }) {
  const perMonth = plan.interval === 'year'
    ? `$${(plan.amount / 100 / 12).toFixed(2)}/mo`
    : null;

  return (
    <div style={{
      flex: 1, minWidth: 200,
      border: plan.popular ? `2px solid ${theme.accent}` : `1px solid ${theme.border}`,
      borderRadius: 12,
      padding: '20px 16px',
      position: 'relative',
      background: plan.popular ? `${theme.accent}08` : 'transparent',
    }}>
      {plan.popular && (
        <div style={{
          position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)',
          background: theme.accent, color: '#fff',
          fontSize: 11, fontWeight: 700, padding: '3px 12px', borderRadius: 20,
          whiteSpace: 'nowrap',
        }}>
          BEST VALUE
        </div>
      )}

      <div style={{ fontWeight: 700, fontSize: 15, color: theme.text, marginBottom: 4 }}>
        {plan.name}
      </div>

      <div style={{ fontSize: 28, fontWeight: 800, color: theme.text, margin: '8px 0 2px' }}>
        ${(plan.amount / 100).toFixed(0)}
        <span style={{ fontSize: 14, fontWeight: 400, color: theme.textMuted }}>
          /{plan.interval}
        </span>
      </div>

      {perMonth && (
        <div style={{ fontSize: 12, color: theme.textMuted, marginBottom: 4 }}>
          {perMonth} billed annually
        </div>
      )}

      {plan.savings && (
        <div style={{ fontSize: 12, color: theme.accent, fontWeight: 600, marginBottom: 8 }}>
          {plan.savings}
        </div>
      )}

      <button
        onClick={onSubscribe}
        disabled={disabled}
        style={{
          marginTop: 12,
          background: plan.popular ? theme.accent : 'transparent',
          color: plan.popular ? '#fff' : theme.accent,
          border: `1px solid ${theme.accent}`,
          borderRadius: 8, padding: '10px 16px',
          fontSize: 13, fontWeight: 600,
          cursor: disabled ? 'not-allowed' : 'pointer',
          width: '100%', opacity: disabled ? 0.7 : 1,
        }}
      >
        {loading ? 'Redirecting…' : 'Start free trial'}
      </button>
    </div>
  );
}
